/**
 * Shared utilities for formatting schedule item dates and times
 */

import { TranslateFunction } from '@locales/generated';
import { formatDateForLanguage } from '@shared/hooks/useFormattedDate';

export type ScheduleItemDateParts = { date: string; time: string; year: string };
/**
 * Formats a date into separate components for schedule item display.
 * Returns locale-aware formatted strings for month-day, year, and time with optional timezone support.
 *
 * @param date - The date to format
 * @param language - BCP 47 language tag (e.g., 'en-US', 'fr', 'ja'). Defaults to 'en-US'
 * @param timezone - Optional IANA timezone identifier (e.g., 'America/Los_Angeles'). If provided, formats the date in this timezone
 * @returns Object containing three formatted parts, or null if formatting fails:
 *   - date: Month and day (e.g., "Nov 11", "11 nov" in French)
 *   - time: Time with AM/PM for 12-hour locales (e.g., "4:00 PM", "16:00" in 24-hour locales)
 *   - year: Four-digit year (e.g., "2025")
 */
export const formatScheduleItemDate = (date: Date, language: string = 'en-US', timezone?: string): Readonly<ScheduleItemDateParts | null> => {
  const formattedDate = formatDateForLanguage(language, date, 'MMMdd', { timezone });
  const formattedTime = formatDateForLanguage(language, date, 'h:mm', { timezone });
  const formattedYear = formatDateForLanguage(language, date, 'yyyy', { timezone });
  if (formattedDate && formattedTime && formattedYear) {
    return {
      date: formattedDate,
      time: formattedTime,
      year: formattedYear,
    };
  }
  return null;
};

export const formatScheduleTimeframe = (args: {
  start?: ScheduleItemDateParts;
  end?: ScheduleItemDateParts;
  timeframeMultiDay: TranslateFunction;
  timeframe: TranslateFunction;
  language?: string;
  timezoneAbbr?: string;
}): string | undefined => {
  const { start, end, timeframeMultiDay, timeframe, timezoneAbbr } = args;

  if (!start || !end) {
    return undefined;
  }

  // Detect multi-day events by comparing formatted date strings.
  // Since both dates are formatted with the same locale and timezone, identical strings indicate the same day.
  // Example: "Nov 11 8:00 PM to Nov 12 2:00 AM" (different dates) vs "Nov 11 8:00 PM to 11:00 PM" (same date)
  const isMultiDay = start.date !== end.date || start.year !== end.year;

  if (isMultiDay) {
    return timeframeMultiDay({
      startDate: start.date,
      startTime: start.time,
      endDate: end.date,
      endTime: end.time,
      ...(timezoneAbbr && { timezoneAbbr }),
    });
  }

  return timeframe({
    start: start.time,
    end: end.time,
    ...(timezoneAbbr && { timezoneAbbr }),
  });
};
